import type { Translation } from '../../types';
import type { services as en } from '../en/services';

export const services: Translation<typeof en> = {
  crumbs: { business: 'Kinh doanh', settings: 'Tích hợp', compliance: 'Tuân thủ' },
  common: {
    refresh: 'Làm mới',
    reason: 'Lý do',
    yes: 'Có',
    no: 'Không',
    none: '—',
    days: '{{count}} ngày',
    years: '{{count}} năm',
    save: 'Lưu',
    edit: 'Sửa',
    loadFailed: 'Không tải được dữ liệu',
    effectiveFrom: 'Hiệu lực từ',
    effectiveFromHint: 'Để trống = ngay bây giờ. Ngày sau sẽ lên lịch phiên bản.',
    done: 'Đã lưu.',
  },
  enum: {
    billing: { ONE_TIME: 'Một lần mỗi nghiệp vụ', RECURRING: 'Theo năm × số năm' },
    mode: { MANDATORY: 'Bắt buộc', DEFAULT: 'Tự thêm (bỏ được)', OPTIONAL: 'Khách tự đặt', OFF: 'Không cung cấp' },
    tldClass: { VN: 'Tên miền .vn', GTLD: 'gTLD', NEW_GTLD: 'gTLD mới', CCTLD: 'ccTLD khác', OTHER: 'Khác' },
    ruleType: { FIXED_PRICE: 'Giá cố định', MARKUP_FIXED: 'Giá vốn + khoản cố định', MARKUP_PERCENT: 'Giá vốn + %' },
    apply: { IMMEDIATE: 'Áp dụng ngay khi đặt', MANUAL: 'Nhân viên / yêu cầu áp dụng' },
    tax: { VAT: 'VAT', KCT: 'Không chịu thuế (KCT)' },
    lineKind: { REGISTRY_FEE: 'Lệ phí', REGISTRY_MAINTENANCE: 'Phí duy trì', SERVICE: 'Phí dịch vụ', ADDON: 'Dịch vụ thêm' },
    operation: { REGISTER: 'Đăng ký', RENEW: 'Gia hạn', TRANSFER: 'Chuyển về', RESTORE: 'Khôi phục', ADDON: 'Dịch vụ thêm' },
    addonState: { ORDERED: 'Đã đặt (tạm giữ tiền)', ACTIVE: 'Đang dùng', CANCELLED: 'Đã huỷ', ENDED: 'Đã kết thúc' },
    transferBasis: { FREE: 'Miễn phí', RENEWAL: 'Giá gia hạn × số năm', OWN_PRICE: 'Giá chuyển về riêng' },
    restoreBasis: { FREE: 'Miễn phí', RENEWAL: 'Giá gia hạn × số năm', OWN_PRICE: 'Giá khôi phục riêng', OWN_PRICE_PLUS_RENEWAL: 'Giá khôi phục + gia hạn' },
    onExpiry: { PARKING_NS: 'Chuyển sang máy chủ tên tạm', REGISTRY_HANDLES: 'Cơ quan quản lý tự xử lý', CLIENT_HOLD: 'clientHold', NONE: 'Không làm gì' },
    pendingStatus: { PENDING: 'Chờ xác nhận', CONFIRMED: 'Đã xác nhận', REJECTED: 'Đã từ chối', EXECUTED: 'Đã thực hiện', FAILED: 'Thất bại' },
    changeType: {
      NAMESERVERS: 'Máy chủ tên',
      DNSSEC: 'DNSSEC',
      TRANSFER_LOCK: 'Khoá chuyển đi',
      AUTH_INFO: 'Mã chuyển đổi',
      RENEW: 'Gia hạn',
      DELETE: 'Xoá',
      TRANSFER_OUT_APPROVE: 'Duyệt chuyển đi',
      REGISTRANT_CHANGE: 'Đổi chủ thể',
      ADDON_ORDER: 'Đặt dịch vụ',
      ADDON_CANCEL: 'Huỷ dịch vụ',
      RESTORE: 'Khôi phục',
    },
    attentionKind: { PROTECTED_CHANGE_PENDING: 'Thay đổi chờ xác nhận', PROTECTED_DOMAIN_EXPIRY: 'Giai đoạn hết hạn', PROTECTED_TRANSFER_OUT: 'Yêu cầu chuyển đi' },
    parkingState: { PARKING: 'Đang chuyển', PARKED: 'Đã chuyển', UNPARKING: 'Đang khôi phục', UNPARKED: 'Đã khôi phục', ABANDONED: 'Bỏ qua' },
    ncCase: { HOLDER_TRANSFER: 'Chuyển nhượng', SUBJECT_CONVERSION: 'Chuyển đổi chủ thể', NAME_CORRECTION: 'Sửa tên chủ thể' },
    ncSubCase: {
      GIFT_CONTRIBUTION_INHERITANCE: 'Tặng cho, góp vốn, thừa kế',
      AUTHORITY_RENAME: 'Đổi tên tổ chức (giữ mã số doanh nghiệp)',
      DIVISION_MERGER: 'Chia, tách, sáp nhập, hợp nhất',
      REORGANISATION_SALE: 'Tổ chức lại, bán, góp vốn',
    },
    ncStatus: {
      DRAFT: 'Đang bổ sung hồ sơ',
      SUBMITTED: 'Đang xét duyệt',
      CORRECTION_REQUESTED: 'Yêu cầu bổ sung',
      APPROVED: 'Đã chấp thuận',
      REGISTRY_CONTACT: 'Đang tạo contact tại VNNIC',
      PANEL_PENDING: 'Chờ nhập trên hệ thống VNNIC',
      COMPLETED: 'Hoàn tất',
      REJECTED: 'Từ chối',
      CANCELLED: 'Đã huỷ',
    },
    ncStage: { BEFORE_APPROVAL: 'Trước khi chấp thuận', AFTER_APPROVAL: 'Sau khi chấp thuận' },
    ncCheck: {
      NOT_PROTECTED: 'Không phải tên miền bảo vệ',
      NO_LEGAL_LOCK: 'Không bị khoá theo pháp luật',
      NO_OPEN_DISPUTE: 'Không có tranh chấp / khiếu nại',
      NOT_SUSPENDED: 'Không bị tạm ngừng',
      DOMAIN_STATE: 'Trạng thái tên miền cho phép',
      SAME_HOLDER: 'Cùng chủ thể',
      CONSENT_BOTH_HOLDERS: 'Hai bên đã đồng ý',
      NEW_HOLDER_VERIFIED: 'Chủ thể mới đã xác thực (VN_SUBJECT_VERIFICATION)',
      DOCUMENTS_BEFORE_APPROVAL: 'Hồ sơ trước khi chấp thuận',
      ENTERPRISE_CODE_MATCH: 'Mã số doanh nghiệp không đổi',
      DECLARATION_SIGNED: 'Bản khai theo tên mới đã ký',
    },
    checkStatus: { PASS: 'Đạt', FAIL: 'Không đạt', WARN: 'Lưu ý' },
    ncDecision: { APPROVE: 'Chấp thuận', REQUEST_CORRECTION: 'Yêu cầu bổ sung', REJECT: 'Từ chối' },
    ncEvent: {
      OPENED: 'Mở yêu cầu',
      STATUS: 'Trạng thái',
      DOCUMENT_UPLOADED: 'Tải lên giấy tờ',
      CONSENT_RECORDED: 'Ghi nhận đồng ý',
      SUBMITTED: 'Gửi xét duyệt',
      WAITING_DOCUMENTS: 'Chờ giấy tờ',
      CONTACT_CREATED: 'Đã tạo contact tại VNNIC',
      PANEL_SUBMITTED: 'Đã nhập trên hệ thống VNNIC',
      LOCKED: 'Đã khoá tên miền (client transfer / update / delete)',
      LOCK_FAILED: 'Khoá tên miền không thành công',
      DECLARATION_ATTACHED: 'Tạo bản khai theo tên mới',
      SPELLING_CORRECTION_MARKED: 'Nhân viên xác nhận chỉ sửa lỗi chính tả',
    },
    consentSide: { CURRENT_REGISTRANT: 'Chủ thể hiện tại', NEW_REGISTRANT: 'Chủ thể mới' },
    consentMethod: { IN_PERSON: 'Trực tiếp', SIGNED_DOCUMENT: 'Văn bản có chữ ký', DIGITAL_SIGNATURE: 'Chữ ký số', EMAIL_LINK: 'Đường dẫn e-mail', PHONE_CALL: 'Điện thoại' },
    signedBy: { BOTH_PARTIES: 'Hai bên', TRANSFEROR: 'Bên chuyển nhượng', TRANSFEREE: 'Bên nhận chuyển nhượng', HOLDER: 'Chủ thể', NEW_HOLDER: 'Chủ thể mới', AUTHORITY: 'Cơ quan có thẩm quyền' },
    panelStatus: { PENDING: 'Cần nhập trên hệ thống', SUBMITTED: 'Đã nhập, chờ VNNIC', COMPLETED: 'VNNIC đã ghi nhận', REFUSED: 'VNNIC từ chối', CANCELLED: 'Đã huỷ' },
    panelOperation: { NAME_CHANGE: 'Đổi tên chủ thể', REGISTRY_LOCK: 'Khoá tên miền', REGISTRY_UNLOCK: 'Mở khoá tên miền' },
    panelExecutor: { MANUAL_PANEL: 'Nhân viên nhập trên hệ thống VNNIC', VNNIC_API: 'API của VNNIC' },
  },
  catalogue: {
    title: 'Dịch vụ tên miền',
    subtitle: 'Danh mục dịch vụ thêm (kể cả lệ phí VNNIC, phí ICANN) và dịch vụ vòng đời. Bật dịch vụ và đặt giá cho từng TLD ở trang TLD (tab Giá & dịch vụ), nơi duy nhất sửa được.',
    fileTitle: 'Danh mục dịch vụ',
    fileBody: 'Danh mục phiên bản {{version}}, do đội kỹ thuật cập nhật. Ở đây chỉ xem; bật dịch vụ và đặt giá ở trang từng TLD.',
    lifecycle: 'Dịch vụ vòng đời',
    lifecycleHint: 'Dịch vụ thêm có thể tính kèm mỗi nghiệp vụ (bắt buộc hoặc tự thêm ở ít nhất một TLD).',
    col: {
      operation: 'Dịch vụ',
      rules: 'Quy tắc giá đang áp dụng',
      components: 'Dòng phí nhà đăng ký gốc',
      addons: 'Dịch vụ thêm tính kèm',
      code: 'Mã',
      name: 'Dịch vụ',
      billing: 'Cách tính phí',
      operations: 'Nghiệp vụ áp dụng',
      apply: 'Áp dụng',
      tax: 'Thuế',
      hints: 'Mặc định theo loại TLD',
      tlds: 'TLD đang cung cấp (* bắt buộc)',
      prices: 'Giá theo TLD',
      domains: 'Tên miền đang dùng',
      state: 'Trạng thái',
    },
    billingHint: { ONE_TIME: 'Thu một lần cho mỗi nghiệp vụ.', RECURRING: 'Nhân số năm của nghiệp vụ (đăng ký / gia hạn N năm → N lần).' },
    addons: 'Danh mục dịch vụ thêm',
    addonsHint: 'Không có giá chung: mỗi TLD có giá riêng. Dịch vụ bắt buộc chưa có giá thì TLD đó không báo giá được.',
    noTld: 'Chưa TLD nào bật',
    percentOfCost: '{{value}} % giá vốn',
    allTlds: 'Mọi TLD',
    free: 'Miễn phí',
    noPrice: 'Chưa có giá',
    inactive: 'Ngừng',
    components: 'Thành phần phí nhà đăng ký gốc (cũ)',
    componentsHint: 'Còn hiệu lực từ trước khi lệ phí chuyển thành dịch vụ bắt buộc. Nên thay bằng dịch vụ bắt buộc và giá theo TLD.',
    col2: { tld: 'TLD', operation: 'Thao tác', component: 'Thành phần', kind: 'Dòng', amount: 'Số tiền', perYear: 'Theo năm', tax: 'Thuế', from: 'Từ' },
  },
  tldAddons: {
    title: 'Dịch vụ của {{zone}}',
    subtitle: 'Dịch vụ nào có ở TLD này và theo cách nào; giá đặt ở tab Giá.',
    applyDefaults: 'Bật các dịch vụ mặc định',
    applyDefaultsDescription: 'Bật các dịch vụ mặc định cho loại TLD {{cls}} chưa được đặt ở TLD này (phí đã gồm trong giá của nhà đăng ký trung gian thì không bật). Dịch vụ đã đặt giữ nguyên.',
    appliedDefaults: 'Đã bật {{count}} dịch vụ.',
    appliedNone: 'Không có dịch vụ nào cần bật thêm.',
    col: { service: 'Dịch vụ', mode: 'Ở TLD này', hint: 'Mặc định', ops: 'Nghiệp vụ', billing: 'Cách tính', tax: 'Thuế', price: 'Giá', domains: 'Tên miền dùng' },
    change: 'Đổi',
    changeTitle: '{{name}} ở {{zone}}',
    changeDescription: 'Bắt buộc: tự tính vào mọi báo giá của nghiệp vụ áp dụng, khách không bỏ được. Tự thêm: có sẵn khi đăng ký / chuyển về, khách bỏ được. Khách tự đặt: chỉ khi khách đặt.',
    modeField: 'Cách cung cấp',
    missingPrice: 'Chưa có giá: báo giá bị từ chối',
    priced: 'Đã có giá',
    noPriceNeeded: '—',
    history: 'Lịch sử thay đổi',
    noHistory: 'Chưa có thay đổi nào.',
    offered: '{{count}} dịch vụ đang cung cấp',
    mandatoryMissing: '{{count}} dịch vụ bắt buộc chưa có giá — báo giá TLD này bị từ chối cho đến khi đặt giá.',
    empty: 'TLD này chưa cung cấp dịch vụ nào. Bấm "Bật các dịch vụ mặc định" hoặc bật từng dịch vụ.',
  },
  tldPrices: {
    addons: 'Giá dịch vụ thêm của {{zone}}',
    addonsHint: 'Giá riêng của TLD này. Phí thu hộ (lệ phí VNNIC, phí ICANN) là giá vốn của dịch vụ: khách trả đúng số đó khi bảng giá không có giá bán riêng.',
    noneOffered: 'TLD này chưa cung cấp dịch vụ nào (tab Dịch vụ).',
    col: { service: 'Dịch vụ', mode: 'Ở TLD này', cost: 'Giá vốn / phí thu hộ', sell: 'Giá bán theo bảng giá', actions: '' },
    setCost: 'Đặt giá',
    setSell: 'Giá bán',
    atCost: 'Không có: thu đúng giá vốn',
    missing: 'Chưa có giá',
    percent: '{{value}} % giá vốn nghiệp vụ',
    forOp: 'khi {{op}}',
    breakdown: 'Giá theo nghiệp vụ',
    breakdownHint: 'Như một báo giá cho TLD này: phí dịch vụ + các dịch vụ bắt buộc / tự thêm = tổng, mỗi dòng với loại thuế.',
    book: 'Bảng giá',
    years: 'Số năm',
    line: { kind: 'Dòng', item: 'Khoản', amount: 'Trước thuế', tax: 'Thuế', total: 'Cộng' },
    total: 'Tổng',
    cannotPrice: 'Không báo giá được',
  },
  tld: {
    title: 'Cấu hình TLD',
    subtitle: 'Điều kiện chuyển về, khôi phục và vòng đời hết hạn theo từng TLD, có phiên bản với ngày hiệu lực và lý do. TLD chưa cấu hình dùng tệp chính sách hết hạn.',
    parking: 'Máy chủ tên tạm (parking)',
    parkingHint: 'Khi hết hạn, TLD dùng "máy chủ tên tạm" chuyển sang cặp này (máy chủ tên và DS đã lưu được khôi phục khi gia hạn trong thời gian chờ gia hạn).',
    editParking: 'Đổi cặp máy chủ tên tạm',
    parkingField: 'Máy chủ tên (mỗi dòng một máy)',
    defaultNs: 'Máy chủ tên mặc định khi đăng ký',
    defaultNsHint: 'Đơn đăng ký không ghi máy chủ tên sẽ dùng bộ này (đơn đã đặt trước đó giữ máy chủ tên của mình).',
    editDefaultNs: 'Đổi máy chủ tên mặc định',
    nsTooFew: 'Cần từ 2 đến 13 máy chủ tên.',
    col: { zone: 'TLD', transfer: 'Chuyển về', restore: 'Khôi phục', stages: 'Chờ gia hạn / chờ restore / chờ xoá', onExpiry: 'Khi hết hạn', autoRenew: 'Nhà đăng ký gốc tự gia hạn', version: 'Phiên bản', state: 'Trạng thái' },
    policyFiles: 'Tệp chính sách',
    notAllowed: 'Không cho phép',
    addsYears: '+{{count}} năm',
    argp: 'ARGP {{argp}} ngày, xoá trước khi hết {{margin}} ngày',
    stageSkipped: '0 = bỏ qua',
    termsTitle: 'Vòng đời & chuyển / khôi phục của {{zone}}',
    termsSubtitle: 'Điều kiện riêng của TLD này; mỗi thay đổi là một phiên bản mới có ngày hiệu lực và lý do.',
    inForceSince: 'Phiên bản {{version}} đang áp dụng từ {{from}} — {{reason}}',
    noSettingsTitle: 'Chưa có cấu hình riêng',
    noSettings: 'TLD này đang theo tệp chính sách hết hạn / chuyển mặc định. Bấm "Sửa cấu hình" để đặt điều kiện riêng.',
    scheduledNote: 'Có {{count}} phiên bản đã lên lịch, sẽ áp dụng sau.',
    versions: 'Các phiên bản',
    noVersions: 'Chưa có phiên bản nào.',
    inForce: 'Đang áp dụng',
    scheduled: 'Đã lên lịch',
    superseded: 'Đã thay',
    skipped: 'bỏ qua giai đoạn',
    parkingShared: 'Cặp máy chủ tên tạm dùng chung cho mọi TLD có "DNS khi hết hạn" = chuyển sang máy chủ tên tạm.',
    parkingWhere: 'Đổi ở Cài đặt › Tên miền & TLD',
    sections: { transfer: 'Chuyển về', restore: 'Khôi phục', expiry: 'Hết hạn', reminders: 'Nhắc và tự gia hạn' },
    edit: 'Sửa cấu hình',
    editTitle: 'Cấu hình TLD · .{{zone}}',
    errp: 'gTLD: kiểm tra mức tối thiểu ERRP của ICANN (ngắt DNS ≥ 8 ngày trước khi xoá, chờ restore 30 ngày có khôi phục, nhắc ~1 tháng và ~1 tuần trước và trong 5 ngày sau khi hết hạn).',
    form: {
      transferAllowed: 'Cho phép chuyển về',
      transferYears: 'Số năm cộng thêm',
      transferMax: 'Khách được chọn tối đa (năm)',
      transferBasis: 'Giá chuyển về',
      restoreAllowed: 'Cho phép khôi phục',
      restoreYears: 'Số năm cộng khi khôi phục',
      restoreBasis: 'Giá khôi phục',
      grace: 'Chờ gia hạn — gia hạn được (ngày)',
      redemption: 'Chờ restore — khôi phục được (ngày)',
      pendingDelete: 'Chờ xoá (ngày)',
      before: 'Nhắc trước khi hết hạn (ngày, cách nhau dấu phẩy)',
      after: 'Nhắc sau khi hết hạn (ngày)',
      lead: 'Tự gia hạn trước (ngày)',
      onExpiry: 'DNS khi hết hạn',
      autoRenews: 'Nhà đăng ký gốc tự gia hạn khi hết hạn',
      argp: 'Thời gian ân hạn tự gia hạn (ngày)',
      margin: 'Xoá trước khi hết ân hạn (ngày)',
    },
  },
  protected: {
    title: 'Tên miền bảo vệ cần xử lý',
    subtitle: 'Tên miền bảo vệ không bao giờ bị thay đổi tự động: mọi thay đổi chờ nhân viên khác người yêu cầu xác nhận, và các giai đoạn hết hạn được xử lý thủ công.',
    protectedCount: 'Tên miền bảo vệ',
    openItems: 'Việc đang mở',
    pendingCount: 'Thay đổi chờ xác nhận',
    attention: 'Cần xử lý',
    pending: 'Thay đổi chờ xác nhận',
    col: { domain: 'Tên miền', kind: 'Việc', summary: 'Chi tiết', opened: 'Mở lúc', state: 'Vòng đời', expires: 'Hết hạn', change: 'Thay đổi', requested: 'Người yêu cầu', at: 'Lúc', status: 'Trạng thái', actions: 'Thao tác' },
    confirm: 'Xác nhận',
    confirmTitle: 'Xác nhận và thực hiện thay đổi',
    confirmWarning: 'Thay đổi được gửi tới nhà đăng ký gốc ngay. Bạn không thể xác nhận yêu cầu của chính mình.',
    reject: 'Từ chối',
    rejectTitle: 'Từ chối thay đổi',
    executed: 'Xong: {{status}}',
    rejected: 'Đã từ chối.',
    empty: 'Không có việc cần xử lý.',
  },
  domain: {
    title: 'Dịch vụ đang dùng',
    subtitle: 'Các dịch vụ thêm của tên miền. Dịch vụ bắt buộc không huỷ được.',
    col: { service: 'Dịch vụ', state: 'Trạng thái', period: 'Thời hạn', billing: 'Tính phí', actions: '' },
    mandatory: 'Bắt buộc',
    empty: 'Tên miền chưa dùng dịch vụ nào.',
    order: 'Đặt dịch vụ',
    orderTitle: 'Đặt dịch vụ cho tên miền',
    orderHint: 'Tính vào tài khoản của khách hàng sở hữu tên miền (tạm giữ tiền đến khi dịch vụ được áp dụng).',
    service: 'Dịch vụ',
    yearsField: 'Số năm (dịch vụ theo năm; để trống = đến ngày hết hạn tên miền)',
    ordered: 'Đã đặt: {{state}}',
    awaiting: 'Tên miền được bảo vệ: đơn đặt chờ nhân viên xác nhận.',
    apply: 'Áp dụng',
    applyTitle: 'Dịch vụ đã thực hiện: tính phí',
    cancel: 'Huỷ',
    cancelTitle: 'Huỷ dịch vụ',
    cancelWarning: 'Dịch vụ đã đặt được hoàn tiền tạm giữ; dịch vụ đang dùng kết thúc, không hoàn tiền.',
    protection: 'Tên miền bảo vệ',
    protectedOn: 'Đang bảo vệ: mọi thay đổi chờ nhân viên xác nhận; không có gì tự động khi hết hạn.',
    protectedOff: 'Không được bảo vệ.',
    protect: 'Bảo vệ',
    protectTitle: 'Bảo vệ tên miền này',
    unprotect: 'Bỏ bảo vệ',
    unprotectTitle: 'Bỏ bảo vệ tên miền',
    pending: 'Thay đổi chờ xác nhận',
    parking: 'Máy chủ tên tạm khi hết hạn',
    parkingInfo: '{{state}} trên {{servers}}; đã lưu: {{saved}} máy chủ tên, {{ds}} bản ghi DS.',
    panelPending: { REGISTRY_LOCK: 'Chờ khoá trên hệ thống VNNIC', REGISTRY_UNLOCK: 'Chờ mở khoá trên hệ thống VNNIC' },
    panelOpen: 'Thao tác trên hệ thống VNNIC',
  },
  nameChange: {
    title: 'Đổi tên / chuyển nhượng',
    subtitle: 'Đổi tên chủ thể và chuyển nhượng tên miền .vn: hồ sơ theo bộ giấy tờ, xét duyệt trong 10 ngày, rồi nhập trên hệ thống quản lý của VNNIC.',
    requests: 'Yêu cầu',
    empty: 'Chưa có yêu cầu.',
    overdue: 'quá hạn',
    stats: { toReview: 'Cần xét duyệt', overdue: 'Quá hạn xét duyệt', panel: 'Chờ nhập trên hệ thống VNNIC' },
    filter: { status: 'Trạng thái', case: 'Trường hợp', any: 'Tất cả', open: 'Chỉ yêu cầu đang mở' },
    col: {
      domain: 'Tên miền',
      case: 'Trường hợp',
      holders: 'Chủ thể → chủ thể mới',
      status: 'Trạng thái',
      due: 'Hạn trả lời',
      panel: 'Hệ thống VNNIC',
      requested: 'Ngày yêu cầu',
      document: 'Giấy tờ',
      stage: 'Thời điểm',
      required: 'Bắt buộc',
      template: 'Mẫu',
      signedBy: 'Người ký',
      present: 'Đã nộp',
      check: 'Kiểm tra',
      result: 'Kết quả',
      blocking: 'Chặn chấp thuận',
      detail: 'Chi tiết',
      file: 'Tệp',
      by: 'Người thực hiện',
      at: 'Thời gian',
      event: 'Sự kiện',
      note: 'Ghi chú',
    },
    formHint: 'VNNIC chỉ đổi tên chủ thể trên hệ thống quản lý của VNNIC. Yêu cầu thu hồ sơ theo trường hợp, được xét duyệt, sau đó tạo contact mới tại VNNIC và nhân viên nhập thay đổi trên hệ thống VNNIC.',
    case: 'Trường hợp',
    subCase: 'Trường hợp cụ thể',
    toSubject: 'Chủ thể mới',
    toSubjectHint: 'Chủ thể thuộc cùng tài khoản khách hàng; tạo trước nếu chưa có.',
    toSubjectSame: 'Sửa tên giữ nguyên chủ thể: chủ thể hiện tại với phiên bản mới đã sửa tên.',
    note: 'Ghi chú',
    loading: 'Đang tải…',
    choose: 'Chọn…',
    close: 'Đóng',
    submitOpen: 'Mở yêu cầu',
    opened: 'Đã mở yêu cầu.',
    openRequest: 'Mở yêu cầu',
    summary: 'Yêu cầu',
    fromHolder: 'Chủ thể hiện tại',
    toHolder: 'Chủ thể mới',
    requirementSet: 'Bộ giấy tờ',
    newerVersion: '(đã có phiên bản mới {{version}}; yêu cầu này giữ phiên bản của mình)',
    dueAt: 'Hạn trả lời (10 ngày)',
    consentCurrent: 'Đồng ý — chủ thể hiện tại',
    consentNew: 'Đồng ý — chủ thể mới',
    waiting: 'Đang chờ',
    addon: 'Dịch vụ (tính phí khi hoàn tất)',
    newContact: 'Mã contact mới tại VNNIC',
    reviewReason: 'Kết quả duyệt',
    closedReason: 'Lý do kết thúc',
    legalBasis: 'Căn cứ pháp lý',
    checklist: 'Giấy tờ (bộ giấy tờ phiên bản {{version}})',
    checks: 'Kiểm tra tự động',
    checksAt: 'Đánh giá lúc {{at}}',
    noChecks: 'Chưa đánh giá.',
    documents: 'Giấy tờ đã nộp',
    noDocuments: 'Chưa có giấy tờ.',
    timeline: 'Diễn biến',
    noEvents: 'Chưa có gì.',
    oneOf: 'Có, hoặc thay bằng: {{others}}',
    or: ' hoặc ',
    present: 'Có ({{count}})',
    absent: 'Còn thiếu',
    upload: 'Tải lên giấy tờ',
    uploadTitle: 'Tải lên giấy tờ theo bộ giấy tờ',
    documentType: 'Giấy tờ',
    file: 'Tệp',
    fileHint: 'PDF, JPEG, PNG hoặc XML, tối đa 20 MB. Được mã hoá cùng giấy tờ hồ sơ.',
    fileType: 'Loại tệp này không được chấp nhận.',
    uploaded: 'Đã tải lên.',
    recordConsent: 'Ghi nhận đồng ý',
    consentTitle: 'Ghi nhận đồng ý của một bên do nhân viên thu thập',
    side: 'Bên',
    method: 'Hình thức',
    consentOne: 'Đã ghi nhận. Đang chờ bên còn lại.',
    consentBoth: 'Hai bên đã đồng ý.',
    submit: 'Gửi xét duyệt',
    submitTitle: 'Gửi yêu cầu để xét duyệt',
    submitHint: 'Dịch vụ được đặt (tạm giữ tiền) và bắt đầu xét duyệt: nhà đăng ký trả lời trong 10 ngày kể từ khi hồ sơ đầy đủ.',
    submitted: 'Đã gửi. Hạn trả lời {{due}}.',
    review: 'Xét duyệt',
    reviewTitle: 'Xét duyệt hồ sơ',
    decision: 'Quyết định',
    decide: 'Ghi nhận quyết định',
    failingChecks: 'Kiểm tra chưa đạt: {{checks}}. Chưa thể chấp thuận.',
    rejectWarning: 'Yêu cầu kết thúc; tiền tạm giữ được hoàn lại.',
    reviewed: 'Đã ghi nhận: {{status}}.',
    vnnicRefused: 'VNNIC từ chối',
    vnnicRefusedTitle: 'Ghi nhận VNNIC từ chối',
    vnnicRefusedWarning: 'Yêu cầu kết thúc với trạng thái từ chối, tiền tạm giữ được hoàn lại và contact mới không được dùng.',
    cancel: 'Huỷ',
    cancelTitle: 'Huỷ yêu cầu',
    cancelWarning: 'Chỉ huỷ được khi yêu cầu chưa gửi tới VNNIC. Tiền tạm giữ được hoàn lại.',
    closed: 'Đã kết thúc.',
    waitingAfter: 'Đã chấp thuận. Đang chờ giấy tờ nộp sau khi chấp thuận: {{docs}}.',
    waitingBefore: 'Còn thiếu trước khi gửi xét duyệt: {{docs}}.',
    panelHint: 'Nhập thay đổi trên hệ thống quản lý của VNNIC với mã contact mới {{handle}}, rồi ghi nhận đã nhập. Yêu cầu tự hoàn tất khi thông báo cập nhật tên miền của VNNIC cho thấy chủ thể mới.',
    panelHintAdmin: 'Tổ chức: người đại diện theo pháp luật là contact quản lý (admin) {{handle}}.',
    adminContact: 'Contact quản lý (người đại diện)',
    completedVia: 'Hoàn tất bởi',
    via: { POLL: 'Thông báo của VNNIC', STAFF: 'Nhân viên xác nhận', RECONCILIATION: 'Đối soát tự động' },
    lockStatus: 'Khoá sau khi đổi tên',
    lock: { PENDING: 'Đang chờ registry xác nhận', APPLIED: 'Đã khoá (transfer / update / delete)', REJECTED: 'Registry từ chối — cần xử lý' },
    confirm: 'Xác nhận hoàn tất',
    confirmTitle: 'Xác nhận VNNIC đã ghi nhận đổi tên',
    confirmHint: 'Dùng khi VNNIC đã xử lý ngoài hệ thống (qua hệ thống quản lý, e-mail, điện thoại). Hệ thống đọc domain:info của VNNIC trước; chỉ hoàn tất khi chủ thể tại VNNIC là contact mới, rồi khoá tên miền.',
    confirmed: 'Đã hoàn tất: tên miền thuộc chủ thể mới và đã được khoá.',
    notYet: 'VNNIC chưa ghi nhận: chủ thể tại VNNIC là {{actual}}, cần là {{expected}}. Yêu cầu vẫn mở.',
    protected: 'Tên miền đang được bảo vệ: không thể yêu cầu đổi tên / chuyển nhượng trực tuyến. Vui lòng liên hệ nhân viên nhà đăng ký để được xử lý thủ công.',
    choice: {
      ORG_RENAME: 'Đổi tên tổ chức (giữ mã số doanh nghiệp)',
      HOLDER_TRANSFER: 'Chuyển nhượng cho chủ thể khác',
      SUBJECT_CONVERSION: 'Chuyển đổi chủ thể khác (tặng cho, chia tách, tổ chức lại…)',
      NAME_CORRECTION: 'Sửa tên chủ thể (lỗi chính tả, cá nhân đổi họ tên)',
      NAME_CORRECTION_ORG: 'Chỉ sửa lỗi chính tả tên tổ chức',
    },
    orgRenameHint: 'Tổ chức đổi tên nhưng giữ nguyên mã số doanh nghiệp: cần Giấy chứng nhận đăng ký doanh nghiệp (đăng ký thay đổi) thể hiện tên mới, hoặc quyết định đổi tên của cơ quan có thẩm quyền, và bản khai theo tên mới cho từng tên miền. Không cần giấy phép kinh doanh cũ. Mã số doanh nghiệp của chủ thể mới phải trùng với chủ thể hiện tại.',
    spellingOnlyHint: 'Chỉ dùng khi tên tổ chức bị sai chính tả. Tổ chức đổi tên thì chọn "Đổi tên tổ chức (giữ mã số doanh nghiệp)".',
    spellingReason: 'Vì sao chỉ là sửa lỗi chính tả',
    spellingReasonHint: 'Bắt buộc với tổ chức; được lưu cùng yêu cầu và nhật ký.',
    toSubjectRename: 'Chủ thể hiện tại với phiên bản mới mang tên mới, hoặc chủ thể có cùng mã số doanh nghiệp.',
    caseMismatch: 'Tổ chức đổi tên nhưng giữ mã số doanh nghiệp không phải "Sửa tên chủ thể": chọn "Đổi tên tổ chức (giữ mã số doanh nghiệp)". "Sửa tên chủ thể" chỉ dùng khi tên bị sai chính tả, kèm lý do.',
    enterpriseChanged: 'Mã số doanh nghiệp của chủ thể mới khác chủ thể hiện tại nên đây không phải đổi tên: chọn "Tổ chức lại, bán, góp vốn" hoặc "Chia, tách, sáp nhập, hợp nhất".',
    spellingMarked: 'Nhân viên xác nhận chỉ sửa lỗi chính tả',
    enterpriseCode: 'Mã số doanh nghiệp',
    ec: { MATCH: 'Trùng khớp', MISMATCH: 'Khác nhau', NOT_APPLICABLE: 'Không có trên hồ sơ' },
    ecHints: '{{current}} → {{next}}',
    ecMismatch: 'Mã số doanh nghiệp đã đổi: không duyệt như đổi tên. Hướng dẫn khách chọn "Tổ chức lại, bán, góp vốn" hoặc "Chia, tách, sáp nhập, hợp nhất".',
    ecNotApplicable: 'Chủ thể hiện tại không có mã số doanh nghiệp trên hồ sơ: đối chiếu quyết định đổi tên của cơ quan có thẩm quyền.',
    declarations: 'Bản khai theo tên mới (từng tên miền)',
    declarationsHint: 'Mỗi tên miền một bản khai, tạo từ tên miền và thông tin mới của chủ thể. Chủ thể ký rồi tải bản đã ký lên ở trang chủ thể; cần bản đã ký được xác minh trước khi chấp thuận.',
    generateDeclaration: 'Tạo bản khai cho tên miền này',
    generateDeclarationTitle: 'Tạo bản khai theo tên mới',
    generateDeclarationHint: 'Bản khai được tạo từ tên miền {{domain}} và thông tin mới của chủ thể.',
    declarationGenerated: 'Đã tạo bản khai.',
    noDeclarations: 'Chưa tạo bản khai cho tên miền này.',
    openHolder: 'Trang chủ thể (tải bản đã ký)',
    generatedPerDomain: 'Tạo tự động cho từng tên miền',
    declStatus: { GENERATED: 'Đã tạo, chờ ký', UPLOADED: 'Đã tải bản ký, đang kiểm tra', VERIFIED: 'Chữ ký số hợp lệ', REVIEW_REQUIRED: 'Chờ nhân viên duyệt bản ký', APPROVED: 'Nhân viên đã duyệt bản ký', REJECTED: 'Bị từ chối' },
    holderDomains: 'Các tên miền .vn của chủ thể hiện tại',
    holderDomainsHint: 'Đổi tên áp dụng cho từng tên miền: mỗi tên miền có yêu cầu và bản khai riêng.',
    noRequest: 'Chưa có yêu cầu',
    thisRequest: 'yêu cầu này',
    col2: { domain: 'Tên miền', form: 'Mẫu', status: 'Trạng thái', generated: 'Tạo lúc', request: 'Yêu cầu', declaration: 'Bản khai' },
  },
  panel: {
    title: 'Thao tác trên hệ thống VNNIC',
    subtitle: 'Các thay đổi nhân viên nhập trên hệ thống quản lý của VNNIC (đổi tên, khoá / mở khoá). Tự hoàn tất khi thông báo cập nhật tên miền và domain:info của VNNIC cho thấy thay đổi.',
    empty: 'Không có thao tác nào.',
    col: { operation: 'Thao tác', expected: 'VNNIC cần thể hiện', status: 'Trạng thái', since: 'Từ lúc', reference: 'Số tham chiếu VNNIC', actions: '' },
    expectRegistrant: 'chủ thể = {{handle}}',
    expectSet: 'đặt: {{statuses}}',
    expectCleared: 'gỡ: {{statuses}}',
    overdue: 'quá hạn',
    recordSubmitted: 'Đã nhập trên hệ thống',
    recordSubmittedTitle: 'Ghi nhận đã nhập thay đổi trên hệ thống VNNIC',
    reference: 'Số tham chiếu VNNIC',
    referenceHint: 'Số yêu cầu hiển thị trên hệ thống VNNIC, nếu có.',
    note: 'Ghi chú',
    submittedDone: 'Đã ghi nhận. Thao tác hoàn tất khi VNNIC ghi nhận thay đổi.',
    checkNow: 'Kiểm tra tại VNNIC',
    notYet: 'VNNIC chưa ghi nhận.',
    refused: 'VNNIC từ chối',
    refusedTitle: 'Ghi nhận VNNIC từ chối',
    refusedWarning: 'Dịch vụ khoá đã đặt được huỷ và tiền tạm giữ được hoàn lại.',
    refusedDone: 'Đã ghi nhận.',
    stepTitle: 'Bước nhập trên hệ thống VNNIC',
    stepHint: 'Nhân viên nhập thay đổi trên hệ thống VNNIC; thông báo cập nhật tên miền (hoặc kiểm tra định kỳ mỗi giờ) sẽ hoàn tất.',
    executor: 'Người thực hiện',
    submittedAt: 'Nhập lúc',
    lastChecked: 'Kiểm tra lần cuối',
    observed: 'VNNIC thể hiện',
  },
  requirements: {
    title: 'Hồ sơ đổi tên',
    subtitle: 'Giấy tờ cần có theo từng trường hợp (NĐ 115/2025, TT 48/2025, VNNIC). Mỗi thay đổi là một phiên bản mới kèm ngày hiệu lực và lý do; yêu cầu đang mở giữ phiên bản lúc mở.',
    inForce: 'Phiên bản {{version}} đang áp dụng',
    reviewDaysValue: 'Xét duyệt trong {{count}} ngày',
    newVersion: 'Phiên bản mới',
    newVersionTitle: 'Ban hành phiên bản mới',
    versionHint: 'Điền sẵn từ phiên bản mới nhất. Không ảnh hưởng yêu cầu đang mở.',
    titleVi: 'Tiêu đề (tiếng Việt)',
    titleEn: 'Tiêu đề (tiếng Anh)',
    legalBasis: 'Căn cứ pháp lý',
    reviewDays: 'Số ngày trả lời',
    documents: 'Giấy tờ',
    code: 'Mã',
    stage: 'Thời điểm',
    oneOf: 'Nhóm thay thế',
    oneOfHint: 'Các giấy tờ cùng nhóm: chỉ cần một (ví dụ TAX).',
    nameVi: 'Tên (tiếng Việt)',
    nameEn: 'Tên (tiếng Anh)',
    template: 'Mẫu',
    signedBy: 'Người ký',
    required: 'Bắt buộc',
    perDomain: 'Bản khai tạo cho từng tên miền',
    remove: 'Xoá',
    addDocument: 'Thêm giấy tờ',
    publish: 'Ban hành',
    published: 'Đã ban hành phiên bản {{version}}.',
  },
  tldPricing: {
    advanced: 'Tuỳ chỉnh nâng cao (giá vốn, phí & dịch vụ, giá bán theo bảng giá)',
    title: 'Giá & dịch vụ của {{zone}}',
    subtitle: 'Mọi khoản TLD này thu, ở một chỗ: giá vốn registry, phí bắt buộc, dịch vụ thêm và giá bán.',
    summary: 'Tóm tắt giá theo nghiệp vụ',
    summaryHint: 'Khách của bảng giá {{book}} trả gì cho 1 năm: phí dịch vụ + phí bắt buộc = tổng, mỗi dòng với loại thuế.',
    book: 'Bảng giá',
    registryCost: 'Giá vốn registry',
    registryCostNote: 'chi phí của mình, không phải dòng của khách',
    total: 'Khách trả (1 năm)',
    tax: 'Thuế',
    cannotPrice: 'Chưa báo giá được',
    feeMissing: '{{name}}: chưa có giá',
    noBooks: 'Chưa có bảng giá nào đang dùng: không báo giá được.',
    costs: 'Giá vốn registry',
    costsHint: 'Registry thu mình bao nhiêu cho mỗi nghiệp vụ, theo năm.',
    noCosts: 'TLD này chưa có giá vốn: chưa nghiệp vụ nào báo giá được.',
    from: 'từ {{date}}',
    fees: 'Phí & dịch vụ thêm',
    feesHint: 'Mỗi dịch vụ đang bật ở TLD này: cách cung cấp, giá vốn / phí thu hộ và giá bán.',
    col: { service: 'Dịch vụ', mode: 'Chế độ', cost: 'Giá vốn / phí thu hộ', sell: 'Giá bán', actions: '' },
    billing: { ONE_TIME: 'một lần', RECURRING: 'theo năm' },
    mode: { MANDATORY: 'Bắt buộc', DEFAULT: 'Tự thêm', OPTIONAL: 'Tuỳ chọn', OFF: 'Tắt' },
    modeHelp: {
      MANDATORY: 'Tự tính vào mọi báo giá của nghiệp vụ áp dụng, khách không bỏ được.',
      DEFAULT: 'Có sẵn khi đăng ký / chuyển về, khách bỏ được.',
      OPTIONAL: 'Chỉ khi khách đặt.',
      OFF: 'Không cung cấp ở TLD này.',
    },
    differs: 'khác gợi ý mặc định',
    differsTitle: 'Mặc định cho loại TLD này: {{hint}}',
    noHint: 'không cung cấp',
    chargedWith: 'Tính kèm: {{ops}}',
    atCost: '= giá vốn',
    missing: 'Chưa có giá',
    free: 'Miễn phí',
    editPrice: 'Sửa giá',
    modeTitle: '{{name}} ở {{zone}}',
    modeTo: 'Chế độ mới: {{mode}}',
    noneOn: 'TLD này chưa bật dịch vụ nào. Bật ở danh sách bên dưới hoặc bấm "Bật các dịch vụ mặc định".',
    offList: 'Dịch vụ chưa bật ({{count}})',
    turnOn: 'Bật',
    opFees: 'Phí nghiệp vụ',
    opFeesHint: 'Phí của yêu cầu đổi tên chủ thể .vn: thu khi yêu cầu được nộp, không bao giờ tính khi đăng ký, gia hạn hay chuyển về.',
    feeFor: 'Phí của: {{case}}',
    on: 'Đang bật',
    off: 'Đang tắt',
    turnOff: 'Tắt',
    history: 'Lịch sử thay đổi ({{count}})',
    legacyNote: 'Mã cũ không còn trong danh mục chỉ hiện ở đây và trong lịch sử đơn hàng / hoá đơn.',
    sell: 'Giá bán (bảng giá)',
    sellHint: 'Khách của mỗi bảng giá trả bao nhiêu cho 1 năm, trước thuế, chưa gồm phí bắt buộc. Bấm vào một ô để sửa.',
    price: {
      title: 'Giá {{name}} ở {{zone}}',
      description: 'Phiên bản mới bắt đầu từ thời điểm chọn và đóng phiên bản đang áp dụng; báo giá đã đưa giữ nguyên giá.',
      current: 'Đang áp dụng',
      none: 'Chưa có giá.',
      costNew: 'Giá vốn / phí thu hộ mới',
      sellNew: 'Giá bán mới',
      keep: 'Giữ nguyên',
      set: 'Đặt mới',
      amount: 'Số tiền',
      currency: 'Tiền tệ',
      basis: 'Cách tính',
      bases: { FIXED: 'Số tiền cố định', PERCENT_OF_REGISTRY_COST: '% giá vốn của nghiệp vụ' },
      forOp: 'Cho nghiệp vụ',
      everyOp: 'Mọi nghiệp vụ',
      book: 'Bảng giá',
      ruleType: 'Giá bán',
      value: 'Giá trị',
      taxFixed: 'Thuế: {{tax}} (theo danh mục)',
      appliesTo: 'Tính kèm: {{ops}}',
      nothing: 'Chọn giá vốn mới, giá bán mới hoặc cả hai.',
      save: 'Lưu giá',
      saved: 'Đã lưu.',
      gridDescription: 'Giá vốn và giá bán của dịch vụ cho mọi nghiệp vụ trong một lần nhập. Dòng để "Giữ nguyên" không đổi; các dòng đổi được lưu cùng lúc từ thời điểm chọn. Báo giá đã đưa giữ nguyên giá.',
      costs: 'Giá vốn / phí thu hộ theo nghiệp vụ',
      sells: 'Giá bán theo bảng giá',
      sellsHint: 'Một giá bán cho mọi nghiệp vụ của dịch vụ; cột "Khách trả" tính từ giá vốn của từng nghiệp vụ.',
      op: 'Nghiệp vụ',
      method: 'Giá mới',
      preview: 'Khách trả',
      inherit: 'Theo "Mọi nghiệp vụ"',
      endCost: 'Bỏ giá riêng',
      endRule: 'Bán theo giá vốn',
      atCost: 'Bán theo giá vốn',
      nothingGrid: 'Chưa có dòng nào thay đổi.',
      saveCount_one: 'Lưu {{count}} thay đổi',
      saveCount_other: 'Lưu {{count}} thay đổi',
      savedCount_one: 'Đã lưu {{count}} thay đổi.',
      savedCount_other: 'Đã lưu {{count}} thay đổi.',
    },
  },
};
